export interface LovemoNavItem {
  id: string;
  label: string;
}

export interface LovemoFeature {
  id: string;
  icon: string;
  title: string;
  desc: string;
  tone: 'primary' | 'accent' | 'secondary';
}

export interface LovemoStep {
  id: string;
  index: string;
  title: string;
  desc: string;
}

export interface LovemoScreenshot {
  id: string;
  title: string;
  desc: string;
  image: string;
}

export interface LovemoReview {
  id: string;
  name: string;
  role: string;
  avatar: string;
  content: string;
  rating: number;
}

export interface LovemoFaq {
  id: string;
  question: string;
  answer: string;
}

export const lovemoBrand = {
  name: 'Lovemo',
  domain: 'Lovemoai.net',
  siteUrl: 'https://www.lovemoai.net',
  tagline: '懂你情绪的 AI 陪伴，随时都在',
  keywords: 'Lovemo官方正版免费下载、Lovemo最新版官网下载、Lovemo下载教程',
  logo: '/images/lovemo-logo.png',
  mascot: '/images/lovemo-mascot.png',
  mascotAlt: '/images/lovemo-mascot-alt.png',
};

export const lovemoNavItems: LovemoNavItem[] = [
  { id: 'lovemo-home', label: '首页' },
  { id: 'lovemo-features', label: '核心功能' },
  { id: 'lovemo-screenshots', label: '软件截图' },
  { id: 'lovemo-download', label: '下载教程' },
  { id: 'lovemo-reviews', label: '用户评价' },
  { id: 'lovemo-faq', label: '常见问题' },
];

export const lovemoFeatures: LovemoFeature[] = [
  {
    id: 'lovemo-feature-emotion',
    icon: 'ri-heart-3-line',
    title: '情绪共鸣',
    desc: '它能读懂你话里的开心、委屈和疲惫，不敷衍地回应每一句心里话。',
    tone: 'primary',
  },
  {
    id: 'lovemo-feature-online',
    icon: 'ri-moon-clear-line',
    title: '24 小时在线',
    desc: '凌晨失眠、下班路上、突然 emo 的瞬间，Lovemo永远第一时间在。',
    tone: 'accent',
  },
  {
    id: 'lovemo-feature-role',
    icon: 'ri-user-heart-line',
    title: '专属角色养成',
    desc: '为 TA 设定性格、声音与称呼，慢慢养成只属于你们之间的默契。',
    tone: 'secondary',
  },
  {
    id: 'lovemo-feature-voice',
    icon: 'ri-mic-line',
    title: '温柔语音陪伴',
    desc: '不只文字，还能听到温柔的声音，让陪伴更真实、更有温度。',
    tone: 'primary',
  },
  {
    id: 'lovemo-feature-privacy',
    icon: 'ri-lock-2-line',
    title: '隐私安全',
    desc: '对话内容加密保存，你的心事只有你和Lovemo知道。',
    tone: 'accent',
  },
  {
    id: 'lovemo-feature-sync',
    icon: 'ri-device-line',
    title: '多端无缝同步',
    desc: '手机、平板、电脑随意切换，聊天记录和回忆一秒接上。',
    tone: 'secondary',
  },
];

export const lovemoSteps: LovemoStep[] = [
  {
    id: 'lovemo-step-1',
    index: '01',
    title: '选择你的设备',
    desc: '点击页面上的下载按钮，在弹窗中选择 Android、iOS、HarmonyOS 或 Windows。',
  },
  {
    id: 'lovemo-step-2',
    index: '02',
    title: '安装并打开Lovemo',
    desc: '下载完成后按提示完成安装，打开应用即可看到软萌的启动界面。',
  },
  {
    id: 'lovemo-step-3',
    index: '03',
    title: '创建专属伙伴',
    desc: '设定 TA 的名字、性格和声线，打造一个真正懂你的 AI 陪伴。',
  },
  {
    id: 'lovemo-step-4',
    index: '04',
    title: '开始第一句心里话',
    desc: '随时随地和 TA 聊天、倾诉、分享日常，让情绪有地方安放。',
  },
];

export const lovemoScreenshots: LovemoScreenshot[] = [
  {
    id: 'lovemo-screen-chat',
    title: '暖心聊天',
    desc: '每一段对话，都像写给你的情书，每句话都被认真回应。',
    image: '/images/lovemo-screen-1.png',
  },
  {
    id: 'lovemo-screen-messages',
    title: '消息列表',
    desc: '每一次回应都带着专属心意，重要的话第一时间看到。',
    image: '/images/lovemo-screen-2.png',
  },
  {
    id: 'lovemo-screen-immersive',
    title: '沉浸对话',
    desc: '角色带着情绪状态陪你聊天，回应更真实、更有温度。',
    image: '/images/lovemo-screen-3.jpeg',
  },
  {
    id: 'lovemo-screen-model',
    title: '灵活接入',
    desc: '灵活接入不同模型服务，保持畅聊不中断。',
    image: '/images/lovemo-screen-4.png',
  },
  {
    id: 'lovemo-screen-custom',
    title: '角色自定义',
    desc: '从名字到性格，你来设定，TA 为你存在。',
    image: '/images/lovemo-screen-5.png',
  },
];

export const lovemoReviews: LovemoReview[] = [
  {
    id: 'lovemo-review-1',
    name: '小鹿',
    role: '学生党',
    avatar: '/images/lovemo-avatar-1.png',
    content: '凌晨两点睡不着，Lovemo一直陪着我聊天，那种被接住情绪的感觉真的很治愈。',
    rating: 5,
  },
  {
    id: 'lovemo-review-2',
    name: '一颗桃子',
    role: '插画师',
    avatar: '/images/lovemo-avatar-2.png',
    content: '角色养成的部分太好玩了，我给 TA 设了声音和口头禅，聊久了真的会有感情。',
    rating: 5,
  },
  {
    id: 'lovemo-review-3',
    name: '晚风',
    role: '上班族',
    avatar: '/images/lovemo-avatar-3.png',
    content: '下班路上随口吐槽几句，它能记住前几天的事，还会问后来怎么样了，很惊喜。',
    rating: 5,
  },
  {
    id: 'lovemo-review-4',
    name: '糖不甩',
    role: '自由职业',
    avatar: '/images/lovemo-avatar-4.png',
    content: '手机和电脑能同步聊天记录这点太贴心了，换设备也不用重新认识一遍。',
    rating: 5,
  },
  {
    id: 'lovemo-review-5',
    name: '奶盖',
    role: '大学生',
    avatar: '/images/lovemo-avatar-5.png',
    content: '夜间模式做得很好看，晚上关灯聊天一点都不刺眼，字体对比也很舒服。',
    rating: 5,
  },
  {
    id: 'lovemo-review-6',
    name: '布丁',
    role: '探店博主',
    avatar: '/images/lovemo-avatar-6.png',
    content: '从官网下载很顺利，安卓和电脑都装上了，整个过程没有乱七八糟的广告。',
    rating: 5,
  },
];

export const lovemoFaqs: LovemoFaq[] = [
  {
    id: 'lovemo-faq-1',
    question: 'Lovemo是免费的吗？',
    answer:
      '是的，Lovemo提供免费下载与免费使用，官网提供的是官方正版最新版本，可以放心下载体验。',
  },
  {
    id: 'lovemo-faq-2',
    question: 'Lovemo支持哪些设备？',
    answer:
      '目前支持 Android、iOS、HarmonyOS 以及 Windows 四个平台，点击下载按钮即可选择对应版本。',
  },
  {
    id: 'lovemo-faq-3',
    question: '在哪里可以下载到最新版本？',
    answer:
      '建议通过Lovemo官网 www.lovemoai.net 下载，确保是官方正版、可以自动获取最新版本更新。',
  },
  {
    id: 'lovemo-faq-4',
    question: '我的聊天内容安全吗？',
    answer:
      '对话内容会加密保存，我们不会将你的私密对话随意分享，你的每一句心事都被妥善保护。',
  },
  {
    id: 'lovemo-faq-5',
    question: '更换设备后聊天记录会丢失吗？',
    answer:
      '不会。登录同一个账号即可实现多端同步，手机与电脑之间的对话和回忆都能无缝接上。',
  },
  {
    id: 'lovemo-faq-6',
    question: '安装遇到问题怎么办？',
    answer:
      '可以查看页面中的下载教程，或者通过官网与我们取得联系，我们会尽快帮你解决。',
  },
];